import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import bbox from '@turf/bbox';
import { centroid } from '@turf/turf';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, NavigationControl, Popup, Source, GeolocateControl } from 'react-map-gl';
import wellknown, { parse } from 'wellknown';
import satelliteIcon from '../../../assets/images/satellite.png';
import streetsIcon from '../../../assets/images/streets.png';
import PopupContent from './Popup';
import PinLegend from './PinLegend';
import PinPopupContent from './PinPopupContent';
import usePropertyPins from '../../../hooks/usePropertyPins';
import httpos from '../../../helpers/httpos';
import {
  PIN_CLUSTER_COLOR,
  PIN_CLUSTER_MAX_ZOOM,
  PIN_CLUSTER_RADIUS,
  pinHasPoint,
  pinPlacers,
  pinsToGeoJson
} from '../../../helpers/propertyPins';
import {
  PIN_ICON_EXPRESSION,
  PIN_ICON_TIP_INSET,
  PIN_ICON_TIP_TO_TOP,
  installPinIcons
} from '../../../helpers/pinIcon';

import axios from 'axios';

import DrawControl from './draw-control/DrawControl';
import styles from './Map.module.css';
import { useLocation } from 'react-router-dom';

mapboxgl.accessToken =
  'pk.eyJ1IjoibXRheXlhYm1pciIsImEiOiJjbDZuaWQ5Z3QwMTNyM2tsbGs2emtlc21uIn0.cwMj_2X3NoUdcbWnLGiG4g';

/* eslint-disable import/no-webpack-loader-syntax */
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const mapStyles = {
  streets: 'mapbox://styles/mapbox/streets-v9',
  satellite: 'mapbox://styles/mapbox/satellite-v9'
};

export const RESULT_POINT_LAYER_ID = 'wfs-layer';
// A property shown by its own point because GeoServer has no parcel there (e.g.
// Regrid has no city lots for Gladstone, MI): as close as the details page goes.
export const POINT_FOCUS_ZOOM = 17;

/**
 * The search result a clicked result dot stands for: same parcel number, nearest
 * to the dot (parcel numbers repeat across counties, and Mapbox hands back the
 * dot's position slightly rounded).
 */
export function findResultProperty(feature, properties) {
  const [lng, lat] = feature?.geometry?.coordinates || [];
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  const parcelno = feature.properties?.parcelno;
  let best = null;
  let bestDistance = Infinity;
  (properties || []).forEach((property) => {
    if (!property?.id || property.lat === '' || property.long === '') return;
    if (parcelno != null && property.parcel_id !== parcelno) return;
    const distance = (Number(property.long) - lng) ** 2 + (Number(property.lat) - lat) ** 2;
    if (distance < bestDistance) {
      best = property;
      bestDistance = distance;
    }
  });
  return best;
}

// Clicking where there is neither a parcel nor a result dot looks this far around
// for the nearest property point. Points don't sit exactly on their buildings,
// and downtown ones are only metres apart.
export const NEAREST_PROPERTY_RADIUS_M = 35;

/** A GeoJSON square `meters` from (lng, lat) on each side. */
export function boxAround(lng, lat, meters) {
  const dLat = meters / 111320;
  const dLng = meters / (111320 * Math.cos((lat * Math.PI) / 180));
  return {
    type: 'Polygon',
    coordinates: [[
      [lng - dLng, lat - dLat],
      [lng + dLng, lat - dLat],
      [lng + dLng, lat + dLat],
      [lng - dLng, lat + dLat],
      [lng - dLng, lat - dLat]
    ]]
  };
}

/**
 * The property nearest a click, as { id, title, lat, long }, or null. The backend
 * picks the nearest point in the box when given `click_point`; one that predates
 * that answers with any point in the box, and without lat/long.
 */
export async function findNearestProperty(lng, lat) {
  const res = await httpos.post('/createencodedparcelid', {
    parcel_geom: JSON.stringify(boxAround(lng, lat, NEAREST_PROPERTY_RADIUS_M)),
    click_point: [lng, lat]
  });
  const data = res?.data;
  if (data?.status !== 1 || !data.encoded_parcel_id) return null;
  return {
    id: data.encoded_parcel_id,
    title: data.full_address,
    lat: data.lat,
    long: data.long
  };
}

const hasParcel = (data) => Array.isArray(data?.features) && data.features.length > 0;
const EMPTY_FEATURES = { type: 'FeatureCollection', features: [] };

export const PIN_LAYER_ID = 'property-pins-lyr';
export const PIN_CLUSTER_LAYER_ID = 'property-pins-cluster-lyr';
export const PIN_CLUSTER_COUNT_LAYER_ID = 'property-pins-cluster-count-lyr';
const PIN_SOURCE_ID = 'property-pins';
const SHOW_PINS_KEY = 'showJobTrackerPins';
const PLACED_BY_KEY = 'jobTrackerPlacedBy';

// A pin's popup opens above its marker, clear of the whole pin, like a map app.
export const PIN_POPUP_OFFSET = [0, -PIN_ICON_TIP_TO_TOP];
const POPUP_MARGIN = 10;

/**
 * How far to pan ([x, y] for map.panBy) so `rect` sits inside `box`, with a
 * margin, below `topInset` px of the map that something else covers; when it
 * can't fit, its top-left edge wins so the address shows.
 */
export function panToFit(box, rect, { margin = POPUP_MARGIN, topInset = 0 } = {}) {
  const axis = (start, end, boxStart, boxEnd) => {
    if (start < boxStart + margin) return start - boxStart - margin;
    if (end > boxEnd - margin) return Math.min(end - boxEnd + margin, start - boxStart - margin);
    return 0;
  };
  return [
    axis(rect.left, rect.right, box.left, box.right),
    axis(rect.top, rect.bottom, box.top + topInset, box.bottom)
  ];
}

function readShowPins() {
  try {
    return localStorage.getItem(SHOW_PINS_KEY) !== '0';
  } catch {
    return true;
  }
}

/** The viewer's "Placed by" choice: a member id, or null for everyone. */
function readPlacedBy() {
  try {
    const id = parseInt(localStorage.getItem(PLACED_BY_KEY), 10);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

export default function MapComponent(props) {

  const location = useLocation();

  const { ownerProperties } = props;

  /** Map ref */
  const map = useRef();
  // Track readiness in state — React refs don't trigger re-renders, so
  // gating downstream UI on `map.current` alone leaves it stuck at its
  // first-paint value (`undefined`). The Mapbox `load` event fires after
  // the underlying instance is fully usable; flipping this state forces a
  // re-render where DrawControl can finally see a populated ref.
  const [mapLoaded, setMapLoaded] = useState(false);
  // filters parcelids
  const [parcelnumb, setParcelnumb] = useState(props.filterParcelIds || []);

  const [showWMS, setShowWMS] = useState(props.filterParcelIds == null);

  // console.log("showWMS",showWMS)

  const [isDrawToolsActive, setIsDrawToolsActive] = useState(false);

  const [staticFeature, setStaticFeaute] = useState(null);

  const [isMobileScreen, setIsMobileScreen] = useState(false);
  // On the bare search map the search filter row covers the top of the map;
  // the pin legend and popups keep clear of it, like the draw tools.
  const searchBarCover = props.page === 'search' ? (isMobileScreen ? 200 : 100) : 0;

  const [zipBoundary, setZipBoundary] = useState(null);
  const [pntGeoJson, setPntGeoJson] = useState({
    type: 'FeatureCollection',
    features: []
  });

  const [hoveredFeature, setHoveredFeature] = useState({
    type: 'FeatureCollection',
    features: []
  });
  //  console.log('hoveredFeature', hoveredFeature);
  const [viewport, setViewport] = useState({
    longitude: -90,
    latitude: 35,
    zoom: 4.5
  });

  const filterPnts = `parcelnumberraw IN (${parcelnumb
    .map((num) => `'${num}'`)
    .join(',')})`;

  // console.log("fietttttt", filter);
  useEffect(() => {
    // console.log('Filtered Parcelnumb IDs:', parcelnumb);
  }, [parcelnumb]);

  /** Basemaps */
  const [basemap, setBasemap] = useState(
    (location.pathname === '/search-property' && !location.search)
      ? mapStyles.streets
      : props.defaultBasemap || mapStyles.satellite
  );
  const changeBasemap = () => {
    if (basemap === mapStyles.satellite) {
      setBasemap(mapStyles.streets);
    } else {
      setBasemap(mapStyles.satellite);
    }
  };

  /** Focused data:  Layer style*/

  const focusedParcelStyle = props.parcelStyle || {
    id: 'focused',
    type: 'fill',
    paint: {
      'fill-color':
        basemap === mapStyles.satellite ? 'rgba(0,0,0,0)' : '#C7BC9F',
      'fill-outline-color': '#67773F'
    }
  };

  /** Static Feature style style */
  const staticFeatureStyle = {
    id: 'static-feature',
    type: 'fill',
    paint: {
      'fill-color': 'rgba(4, 174, 196, 0.1)', // 50% transparent blue
      'fill-outline-color': 'rgb(4, 174, 196)'
    }
  };


  /** Popup state */
  const [showPopup, setShowPopup] = useState(false);
  const [popupLocation, setPopupLocation] = useState({
    latitude: 0,
    longitude: 0
  });

  const closePopup = () => setShowPopup(true);
  // A clicked result dot with no parcel under it: the popup shows that result.
  const [focusedResult, setFocusedResult] = useState(null);
  const [drawStarted, setDrawStarted] = useState(false);

  const [focusedParcel, setFocusedParcel] = useState({
    type: 'FeatureCollection',
    features: []
  });

  const [detailsParcelGeom, setDetailsParcelGeom] = useState(
    props.detailsParcelGeom || undefined
  );

  /** Job tracker pins: the company's, on every interactive map whatever the search. */
  const { pins, viewerId, ownerId } = usePropertyPins();
  // "Placed by": everyone's pins, or one person's. A choice whose person has no
  // pins on the map (any more) falls back to everyone.
  const placers = useMemo(
    () => pinPlacers((pins || []).filter(pinHasPoint), { viewerId, ownerId }),
    [pins, viewerId, ownerId]
  );
  const [placedByChoice, setPlacedByChoice] = useState(readPlacedBy);
  const placedBy = placers.some((person) => person.id === placedByChoice) ? placedByChoice : null;
  const shownPins = useMemo(
    () => (placedBy === null ? pins : (pins || []).filter((pin) => pin.created_by?.id === placedBy)),
    [pins, placedBy]
  );
  const pinsGeoJson = useMemo(() => pinsToGeoJson(shownPins), [shownPins]);
  // Offered whenever the company has pins on the map, even if the chosen person's
  // are filtered to none, so the choice can always be changed back.
  const pinsEnabled = !props.isStatic && (pins || []).some(pinHasPoint);
  const choosePlacedBy = (id) => {
    setPlacedByChoice(id);
    try {
      if (id === null) localStorage.removeItem(PLACED_BY_KEY);
      else localStorage.setItem(PLACED_BY_KEY, String(id));
    } catch {
      // A blocked storage only costs remembering the choice.
    }
  };
  const [showPins, setShowPins] = useState(readShowPins);
  const [selectedPinId, setSelectedPinId] = useState(null);
  // The pins layer waits for its marker images: a layer that asks for an image
  // before it exists is never redrawn when the image arrives.
  const [pinIconsReady, setPinIconsReady] = useState(false);
  const selectedPin = pinsEnabled && showPins
    ? shownPins.find((pin) => pin.id === selectedPinId && Number.isFinite(pin.latitude)) || null
    : null;

  // An opened pin's popup is panned into view instead of running off the map.
  const selectedPinKey = selectedPin?.id ?? null;
  useEffect(() => {
    if (selectedPinKey === null) return undefined;
    const frame = requestAnimationFrame(() => {
      const container = map.current?.getContainer?.();
      const popup = container?.querySelector(`.${styles.pinPopup}`);
      if (!popup) return;
      const [dx, dy] = panToFit(container.getBoundingClientRect(), popup.getBoundingClientRect(), {
        topInset: searchBarCover
      });
      if (dx || dy) map.current.panBy([dx, dy]);
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedPinKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleShowPins = (visible) => {
    setShowPins(visible);
    if (!visible) setSelectedPinId(null);
    try {
      localStorage.setItem(SHOW_PINS_KEY, visible ? '1' : '0');
    } catch {
      // A blocked storage only costs remembering the choice.
    }
  };


  function isValidBoundingBox(bbox) {
    try {
      if (!Array.isArray(bbox) || bbox.length !== 4) {
        console.error('Invalid bbox structure:', bbox);
        return false;
      }
    
      const [minLng, minLat, maxLng, maxLat] = bbox;
    
      const isValidLngLat = (lng, lat) =>
        typeof lng === 'number' && typeof lat === 'number' &&
        lng >= -180 && lng <= 180 &&
        lat >= -90 && lat <= 90;
    
      // Validate each corner of the bounding box
      return (
        isValidLngLat(minLng, minLat) &&
        isValidLngLat(maxLng, maxLat) &&
        minLng <= maxLng && // Ensure proper bbox bounds
        minLat <= maxLat
      );
    }
    catch (error) {
      console.error('Error validating bounding box:', error);
      return false;
    }
    
  }

  useEffect(() => {
    if (pntGeoJson.features) {
      setHoveredFeature(
        pntGeoJson.features.find(
          (f) =>
            f.properties.parcelno === props.hoverParcelId
        )
      );
    }
  }, [props.hoverParcelId]);

  useEffect(() => {
    if (!showWMS) {
      // showWFSData();
      setTimeout(() => {
        let _pntGeoJson = null;
        if (props.page === 'details') {
          // Single Property Details
          const property = JSON.parse(localStorage.getItem('propertyData'))
          if (property && property.lat && property.long) {
            _pntGeoJson = {
              type: 'FeatureCollection',
              features: [{
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [+property.long, +property.lat]
                },
                properties: {
                  parcelno: property.parcel_id
                }
              }]
            }
          }
        } else if (props.page === 'owner') {
          if (ownerProperties) {
            _pntGeoJson = {
              type: 'FeatureCollection',
              features: ownerProperties.filter(pr => pr.latitude != null && pr.longitude != null).map(property => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [+property.longitude, +property.latitude]
                },
                properties: {
                  parcelno: property.parcel_id
                }
              }))
            }

          }
        }
        else if (props.page !== 'results') {
          // All Filtered Properties
          const listingProperties = JSON.parse(localStorage.getItem('listingData')).parcel_ids;
          const _zipBoundary = JSON.parse(localStorage.getItem('listingData')).zipGeometry;
          if (_zipBoundary && _zipBoundary.length > 0) {
            setZipBoundary(parse(_zipBoundary));
            // console.log('parsed zip' ,parse(_zipBoundary));
          } else {
            setZipBoundary(null);
          }

          if (listingProperties) {
            _pntGeoJson = {
              type: 'FeatureCollection',
              features: listingProperties.filter(prop => prop.long != null && prop.lat != null).map(property => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [+property.long, +property.lat]
                },
                properties: {
                  parcelno: property.parcelno
                }
              }))
            }
          }
        }
        if (_pntGeoJson) {


          setPntGeoJson(_pntGeoJson);

          if (props.page === 'owner' || (props.page === 'results')) {

            if (_pntGeoJson.features.length > 0) {
              const [minLng, minLat, maxLng, maxLat] = bbox(_pntGeoJson);
              const { innerWidth, innerHeight } = window;
              const padding = Math.min(innerWidth, innerHeight)
              map.current.fitBounds(
                [
                  [minLng, minLat],
                  [maxLng, maxLat]
                ],
                { padding: 20, duration: 1000 }
              );
            }
          }
        }
      }, 500)
    }
  }, []);

  const lastMarkerKey = useRef(null);
  useEffect(() => {
    if (props.page !== 'results' || showWMS) return;

    let listing = null;
    try {
      listing = JSON.parse(localStorage.getItem('listingData'));
    } catch (error) {
      return;
    }
    if (!listing || !listing.parcel_ids) return;

    const markerKey = listing.parcel_ids
      .map((property) => property.parcelno)
      .join(',');
    if (!markerKey || markerKey === lastMarkerKey.current) return;
    lastMarkerKey.current = markerKey;

    const _zipBoundary = listing.zipGeometry;
    setZipBoundary(
      _zipBoundary && _zipBoundary.length > 0 ? parse(_zipBoundary) : null
    );

    setPntGeoJson({
      type: 'FeatureCollection',
      features: listing.parcel_ids
        .filter((prop) => prop.long != null && prop.lat != null)
        .map((property) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [+property.long, +property.lat]
          },
          properties: {
            parcelno: property.parcelno
          }
        }))
    });
  }, [props.filterParcelIds, props.page, showWMS]);

  // Auto-fit map to results when a NEW search runs. Keyed on (search_type +
  // search_query) so pagination, filter-only changes, and re-renders don't
  // re-trigger the zoom — only an actual address/query change does.
  const lastFitKey = useRef(null);
  useEffect(() => {
    if (props.page !== 'results') return;
    if (!map.current) return;

    const properties = props.filteredProperties;
    if (!properties || properties.length === 0) return;

    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search_query') || '';
    const searchType = params.get('search_type') || '';
    const boundary = params.get('geometry') || '';
    if (!searchQuery && !boundary) return;

    const fitKey = `${searchType}::${searchQuery}::${boundary}`;
    if (fitKey === lastFitKey.current) return;

    const coords = properties
      .filter((p) => p.lat != null && p.long != null)
      .map((p) => [+p.long, +p.lat]);
    if (coords.length === 0) return;

    const lngs = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    // Address searches should zoom in tight; city/zip/owner stay wider. A single
    // result goes in as close as its own point allows: the parcel lookup below
    // zooms to the parcel, but it can land before this fit (which waits for the
    // map to load) and there may be no parcel at all.
    const maxZoom = coords.length === 1
      ? POINT_FOCUS_ZOOM
      : searchType === 'address' ? 15 : 12;

    map.current.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding: 60, duration: 0, maxZoom }
    );

    lastFitKey.current = fitKey;
  }, [props.filteredProperties, location.search, props.page, mapLoaded]);

  // Auto-pan to the single property on the details page. The existing
  // once-on-mount effect reads localStorage with a 500ms timeout, which
  // races the async property fetch and often misses on a cold cache. This
  // re-fires when filterParcelIds arrives so the camera always centers.
  const lastDetailKey = useRef(null);
  useEffect(() => {
    if (props.page !== 'details') return;
    if (!map.current) return;

    const parcelId = props.filterParcelIds?.[0];
    if (!parcelId || parcelId === lastDetailKey.current) return;

    let property;
    try {
      property = JSON.parse(localStorage.getItem('propertyData'));
    } catch {
      return;
    }
    if (!property || property.lat == null || property.long == null) return;

    map.current.flyTo({
      center: [+property.long, +property.lat],
      zoom: 17,
      duration: 1000,
    });

    lastDetailKey.current = parcelId;
  }, [props.filterParcelIds, props.page]);

  useEffect(() => {
    if ((!!focusedParcel.features.length || focusedResult) && !isDrawToolsActive) {
      setShowPopup(true);
    }
  }, [popupLocation]);

  useEffect(() => {
    if (focusedParcel.features.length > 0) {
      const [minLng, minLat, maxLng, maxLat] = bbox(focusedParcel.features[0]);
      if (isValidBoundingBox([minLng, minLat, maxLng, maxLat])) {
        map.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat]
          ],
          { padding: 100, duration: 0 }
        );
      }

    }
  }, [focusedParcel])

  

  useEffect(() => {

    if ((props.page === 'details' && pntGeoJson.features.length > 0) || (props.page === 'results' && pntGeoJson.features.length === 1)) {

      const lat = pntGeoJson.features[0].geometry.coordinates[1];
      const lng = pntGeoJson.features[0].geometry.coordinates[0];

      const size = 0.0001;

      var parameters = {
        service: 'WMS',
        version: '1.1.1',
        request: 'GetFeatureInfo',
        layers: 'rhinorecon:parcels',
        query_layers: 'rhinorecon:parcels',
        feature_count: 50,
        info_format: 'application/json',
        format_options: 'callback:handleJson',
        SrsName: 'EPSG:4326',
        width: 101,
        height: 101,
        x: 50,
        y: 50,
        bbox:
          lng -
          size +
          ',' +
          (lat - size) +
          ',' +
          (lng + size) +
          ',' +
          (lat + size)
      };
      // No parcel there: zoom to the result's own point instead of staying out.
      const focusPoint = () => {
        if (props.page === 'results') {
          map.current?.jumpTo({ center: [lng, lat], zoom: POINT_FOCUS_ZOOM });
        }
      };
      axios
        .get(`https://geoserverv1.rhinorecon.com/geoserver/rhinorecon/wms`, {
          params: parameters
        })
        .then((res) => {
          if (!hasParcel(res.data)) {
            setFocusedParcel(EMPTY_FEATURES);
            focusPoint();
            return;
          }
          setFocusedParcel(res.data);
        })
        .catch(focusPoint);
    }
  }, [props.page, pntGeoJson]);

  const zoomIntoPinCluster = (feature) => {
    const source = map.current?.getSource(PIN_SOURCE_ID);
    const center = feature.geometry?.coordinates;
    if (!source || !center) return;
    source.getClusterExpansionZoom(feature.properties?.cluster_id, (error, zoom) => {
      if (!error) map.current?.easeTo({ center, zoom });
    });
  };

  // Popup for a property with no parcel polygon: zoom in on its point and open it there.
  const showPropertyAt = (property, center) => {
    setFocusedResult(property);
    map.current.flyTo({
      center,
      zoom: Math.max(map.current.getZoom(), POINT_FOCUS_ZOOM),
      duration: 1000
    });
    setPopupLocation({ latitude: center[1], longitude: center[0] });
  };
  const nearestLookup = useRef(0);

  const handleClick = useCallback((event) => {

    event.preventDefault();
    if (!isDrawToolsActive) {
      // Any click makes an earlier nearest-property lookup stale.
      nearestLookup.current += 1;
      // A pin opens its own popup instead of the parcel lookup underneath it.
      const pinFeature = event.features?.find((feature) => feature.layer?.id === PIN_LAYER_ID);
      if (pinFeature) {
        setShowPopup(false);
        setSelectedPinId(pinFeature.properties?.id ?? null);
        return;
      }
      setSelectedPinId(null);
      // A bubble of pins zooms in until they split apart.
      const clusterFeature = event.features?.find((feature) => feature.layer?.id === PIN_CLUSTER_LAYER_ID);
      if (clusterFeature) {
        setShowPopup(false);
        zoomIntoPinCluster(clusterFeature);
        return;
      }

      const { lng, lat } = event.lngLat;
      const size = 0.0001;
      // The search result dot clicked, if any: it still opens when there is no parcel.
      const resultFeature = event.features?.find((feature) => feature.layer?.id === RESULT_POINT_LAYER_ID);
      const lookup = nearestLookup.current;

      var parameters = {
        service: 'WMS',
        version: '1.1.1',
        request: 'GetFeatureInfo',
        layers: 'rhinorecon:parcels',
        query_layers: 'rhinorecon:parcels',
        feature_count: 50,
        info_format: 'application/json',
        format_options: 'callback:handleJson',
        SrsName: 'EPSG:4326',
        width: 101,
        height: 101,
        x: 50,
        y: 50,
        bbox:
          lng -
          size +
          ',' +
          (lat - size) +
          ',' +
          (lng + size) +
          ',' +
          (lat + size)
      };
      // const mapPadding = props.mapZoomPadding || 0.005;
      axios
        .get(`https://geoserverv1.rhinorecon.com/geoserver/rhinorecon/wms`, {
          params: parameters
        })
        .then((res) => {
          if (!hasParcel(res.data)) {
            // No parcel here (fitting to nothing would throw "Invalid LngLat").
            setFocusedParcel(EMPTY_FEATURES);
            setFocusedResult(null);
            // A search result dot opens from its own point and record.
            const result = resultFeature && findResultProperty(resultFeature, props.filteredProperties);
            if (result) {
              showPropertyAt(result, [Number(result.long), Number(result.lat)]);
              return;
            }
            // Anywhere else, the property nearest the click (a later click wins).
            findNearestProperty(lng, lat)
              .then((property) => {
                if (!property || lookup !== nearestLookup.current) return;
                const center = Number.isFinite(property.lat) && Number.isFinite(property.long)
                  ? [property.long, property.lat]
                  : [lng, lat];
                showPropertyAt(property, center);
              })
              .catch(() => {});
            return;
          }
          setFocusedResult(null);
          setFocusedParcel(res.data);
          const [minLng, minLat, maxLng, maxLat] = bbox(res.data);
          const { innerWidth, innerHeight } = window;
          const padding = Math.min(innerWidth, innerHeight)

          map.current.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            {
              padding: 20,
              duration: 1000
            }
          );
          const _parcelCentroid = centroid(res.data);
          // console.log('_parcelCentroid', _parcelCentroid)
          setPopupLocation({ latitude: _parcelCentroid.geometry.coordinates[1], longitude: _parcelCentroid.geometry.coordinates[0] });
        });
    }
  });

  useEffect(() => {
    // map.fitBounds to the drawn features
    if (props.drawnFeatures && map.current) {
      const drawnFeatures = wellknown.parse(props.drawnFeatures);
      const [minLng, minLat, maxLng, maxLat] = bbox(drawnFeatures);
      const { innerWidth, innerHeight } = window;
      const padding = Math.min(innerWidth, innerHeight)

      map.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        {
          padding: 50,
          duration: 1000
        }
      );
      props.handleSearchClick && props.handleSearchClick();

    }
  }, [props.drawnFeatures, map.current]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const geom = urlParams.get('geometry');

    if (geom && geom !== 'null' && geom !== 'undefined' && parse(geom)) {
      setStaticFeaute(geom);
    }
  }
    , [location.search]);


  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreen(window.innerWidth <= 800);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  return (
    <div
      style={{
        width: '100%',
        height: showWMS
          ? 'calc(100vh - 21rem)'
          : props.page === 'details'
            ? 'calc(100vh - 25rem)'
            : 'calc(100vh - 28rem)'
      }}
    >
      <Map
        ref={map}
        initialViewState={viewport}
        style={{
          width: '100%',
          height: props.mapHeight ? props.mapHeight : '100%',
        }}
        mapStyle={basemap}
        onLoad={(event) => {
          setMapLoaded(true);
          if (event?.target) {
            installPinIcons(event.target);
            setPinIconsReady(true);
          }
        }}
        onClick={!props.isStatic && handleClick}
        interactiveLayerIds={['parcels-wms-lyr', RESULT_POINT_LAYER_ID, PIN_LAYER_ID, PIN_CLUSTER_LAYER_ID]}
      >
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: 10,
            zIndex: 1000,
            padding: '5px',
          }}
        >
          <img
            src="data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 xmlns:xlink=%27http://www.w3.org/1999/xlink%27 fill-rule=%27evenodd%27 viewBox=%270 0 88 23%27%3E%3Cdefs%3E%3Cpath id=%27logo%27 d=%27M11.5 2.25c5.105 0 9.25 4.145 9.25 9.25s-4.145 9.25-9.25 9.25-9.25-4.145-9.25-9.25 4.145-9.25 9.25-9.25zM6.997 15.983c-.051-.338-.828-5.802 2.233-8.873a4.395 4.395 0 013.13-1.28c1.27 0 2.49.51 3.39 1.42.91.9 1.42 2.12 1.42 3.39 0 1.18-.449 2.301-1.28 3.13C12.72 16.93 7 16 7 16l-.003-.017zM15.3 10.5l-2 .8-.8 2-.8-2-2-.8 2-.8.8-2 .8 2 2 .8z%27/%3E%3Cpath id=%27text%27 d=%27M50.63 8c.13 0 .23.1.23.23V9c.7-.76 1.7-1.18 2.73-1.18 2.17 0 3.95 1.85 3.95 4.17s-1.77 4.19-3.94 4.19c-1.04 0-2.03-.43-2.74-1.18v3.77c0 .13-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V8.23c0-.12.1-.23.23-.23h1.4zm-3.86.01c.01 0 .01 0 .01-.01.13 0 .22.1.22.22v7.55c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V15c-.7.76-1.69 1.19-2.73 1.19-2.17 0-3.94-1.87-3.94-4.19 0-2.32 1.77-4.19 3.94-4.19 1.03 0 2.02.43 2.73 1.18v-.75c0-.12.1-.23.23-.23h1.4zm26.375-.19a4.24 4.24 0 00-4.16 3.29c-.13.59-.13 1.19 0 1.77a4.233 4.233 0 004.17 3.3c2.35 0 4.26-1.87 4.26-4.19 0-2.32-1.9-4.17-4.27-4.17zM60.63 5c.13 0 .23.1.23.23v3.76c.7-.76 1.7-1.18 2.73-1.18 1.88 0 3.45 1.4 3.84 3.28.13.59.13 1.2 0 1.8-.39 1.88-1.96 3.29-3.84 3.29-1.03 0-2.02-.43-2.73-1.18v.77c0 .12-.1.23-.23.23h-1.4c-.13 0-.23-.1-.23-.23V5.23c0-.12.1-.23.23-.23h1.4zm-34 11h-1.4c-.13 0-.23-.11-.23-.23V8.22c.01-.13.1-.22.23-.22h1.4c.13 0 .22.11.23.22v.68c.5-.68 1.3-1.09 2.16-1.1h.03c1.09 0 2.09.6 2.6 1.55.45-.95 1.4-1.55 2.44-1.56 1.62 0 2.93 1.25 2.9 2.78l.03 5.2c0 .13-.1.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.8 0-1.46.7-1.59 1.62l.01 4.68c0 .13-.11.23-.23.23h-1.41c-.13 0-.23-.11-.23-.23v-4.59c0-.98-.74-1.71-1.62-1.71-.85 0-1.54.79-1.6 1.8v4.5c0 .13-.1.23-.23.23zm53.615 0h-1.61c-.04 0-.08-.01-.12-.03-.09-.06-.13-.19-.06-.28l2.43-3.71-2.39-3.65a.213.213 0 01-.03-.12c0-.12.09-.21.21-.21h1.61c.13 0 .24.06.3.17l1.41 2.37 1.4-2.37a.34.34 0 01.3-.17h1.6c.04 0 .08.01.12.03.09.06.13.19.06.28l-2.37 3.65 2.43 3.7c0 .05.01.09.01.13 0 .12-.09.21-.21.21h-1.61c-.13 0-.24-.06-.3-.17l-1.44-2.42-1.44 2.42a.34.34 0 01-.3.17zm-7.12-1.49c-1.33 0-2.42-1.12-2.42-2.51 0-1.39 1.08-2.52 2.42-2.52 1.33 0 2.42 1.12 2.42 2.51 0 1.39-1.08 2.51-2.42 2.52zm-19.865 0c-1.32 0-2.39-1.11-2.42-2.48v-.07c.02-1.38 1.09-2.49 2.4-2.49 1.32 0 2.41 1.12 2.41 2.51 0 1.39-1.07 2.52-2.39 2.53zm-8.11-2.48c-.01 1.37-1.09 2.47-2.41 2.47s-2.42-1.12-2.42-2.51c0-1.39 1.08-2.52 2.4-2.52 1.33 0 2.39 1.11 2.41 2.48l.02.08zm18.12 2.47c-1.32 0-2.39-1.11-2.41-2.48v-.06c.02-1.38 1.09-2.48 2.41-2.48s2.42 1.12 2.42 2.51c0 1.39-1.09 2.51-2.42 2.51z%27/%3E%3C/defs%3E%3Cmask id=%27clip%27%3E%3Crect x=%270%27 y=%270%27 width=%27100%25%27 height=%27100%25%27 fill=%27white%27/%3E%3Cuse xlink:href=%27%23logo%27/%3E%3Cuse xlink:href=%27%23text%27/%3E%3C/mask%3E%3Cg id=%27outline%27 opacity=%270.3%27 stroke=%27%23000%27 stroke-width=%273%27%3E%3Ccircle mask=%27url%28%23clip%29%27 cx=%2711.5%27 cy=%2711.5%27 r=%279.25%27/%3E%3Cuse xlink:href=%27%23text%27 mask=%27url%28%23clip%29%27/%3E%3C/g%3E%3Cg id=%27fill%27 opacity=%270.9%27 fill=%27%23fff%27%3E%3Cuse xlink:href=%27%23logo%27/%3E%3Cuse xlink:href=%27%23text%27/%3E%3C/g%3E%3C/svg%3E"
            alt="Mapbox Logo"
            style={{ height: '20px' }}
          />
        </div>
        <NavigationControl position='bottom-right' />
        <GeolocateControl position='bottom-right'
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation={false}
        />
        {mapLoaded && map.current && props.page !== 'details' && (
          <DrawControl
            map={map.current}
            controls={['polygon', 'circle']}
            drawnFeatures={props.drawnFeatures}
            setDrawnFeatures={props.setDrawnFeatures}
            isDrawToolsActive={isDrawToolsActive}
            setIsDrawToolsActive={setIsDrawToolsActive}
            handleSearch={props.handleSearchClick}
            resetFilters={props.resetFilters}
            setPntGeoJson={setPntGeoJson}
            setStaticFeaute={setStaticFeaute}
            setHoveredFeature={setHoveredFeature}
            setShowWMS={setShowWMS}
            setShowowners={props.setShowowners}
            setListingData={props.setListingData}
            Maptoggle={props.Maptoggle}
            setSearchQueryValues={props.setSearchQueryValues}
            setFilteredProperties={props.setFilteredProperties}
            filteredProperties={props.filteredProperties}
            handleSubmit={props.handleSubmit}
            propertyTypeExists={props.propertyTypeExists}
            // Only the bare-URL map (page="search") sits under the
            // overlapping filter row and needs clearance. On mobile the
            // filter row wraps taller, so it needs a larger offset than
            // the single-row desktop layout. Elsewhere it clears the filter
            // bar by 12px using its measured height (100px when the bar is
            // one row; more on iPad widths, where it wraps). The results /
            // owner maps already start well below the filter bar, so keep
            // them at the historical 20px.
            topOffset={
              props.page === 'search'
                ? isMobileScreen
                  ? 200
                  : 'calc(var(--search-filter-bar-height, 88px) + 12px)'
                : 20
            }
          />
        )}
        {showPopup && (
          <Popup
            longitude={popupLocation.longitude}
            latitude={popupLocation.latitude}
            anchor='bottom'
            onClose={() => {
              setShowPopup(false);
              setFocusedParcel({ type: 'FeatureCollection', features: [] });
              setFocusedResult(null);
            }}
          >
            <PopupContent
              feature={
                focusedParcel.features.length && focusedParcel.features[0]
              }
              property={focusedParcel.features.length ? null : focusedResult}
            />
          </Popup>
        )}
        {!props.hideParcelsLyr && (
          <Source
            id='parcels-wms'
            type='raster'
            tiles={[
              `https://geoserverv1.rhinorecon.com/geoserver/rhinorecon/wms?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256&layers=rhinorecon:parcels`
            ]}
            tileSize={256}
          >
            <Layer id='parcels-wms-lyr' type='raster' minzoom={14} />
          </Source>
        )}
        {showWMS && (
          <>
            <Source
              id='points-wms'
              type='raster'
              tiles={[
                `https://geoserverv1.rhinorecon.com/geoserver/rhinorecon/wms?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256&layers=rhinorecon:vw_pnt_property_data_new`
              ]}
              tileSize={256}
            >
              <Layer id='points-wms-lyr' minzoom={14} type='raster' />
            </Source>
          </>
        )}
        {!showWMS && pntGeoJson && (
          <>
            <Source type='geojson' data={pntGeoJson}>
              <Layer
                id={RESULT_POINT_LAYER_ID}
                type='circle'
                minzoom={14}
                paint={{
                  'circle-color': '#67773f',
                  'circle-radius': 7,
                  'circle-stroke-color': '#fff',
                  'circle-stroke-width': 2
                }}
              />
            </Source>

            <Source type='geojson' data={hoveredFeature}>
              {!props.isStatic && (
                <Layer
                  id='wfs-layer2'
                  type='circle'
                  minzoom={14}
                  paint={{
                    'circle-color': 'yellow',
                    'circle-radius': 10,
                    'circle-stroke-color': '#67773f',
                    'circle-stroke-width': 2
                  }}
                />
              )}
            </Source>
          </>
        )}
        {
          zipBoundary && (
            <Source type='geojson' data={zipBoundary}>
              <Layer
                id='zip-boundary'
                type='line'
                paint={{
                  'line-color': '#67773f',
                  'line-width': 2
                }}
              />
            </Source>
          )
        }
        {props.page === 'details' && pntGeoJson && (
          <>
            <Source type='geojson' data={pntGeoJson}>
              <Layer
                id='highlted-point'
                type='circle'
                minzoom={14}
                paint={{
                  'circle-color': 'rgba(0,0,0,0)',
                  'circle-radius': 12,
                  'circle-stroke-color': 'yellow',
                  'circle-stroke-width': 2
                }}
              />
            </Source>
          </>
        )}
        {focusedParcel.features.length > 0 && (
          <Source type='geojson' data={focusedParcel}>
            <Layer
              {...focusedParcelStyle}
              beforeId={showWMS ? 'points-wms-lyr' : 'wfs-layer'}
            />
          </Source>
        )}
        {staticFeature && props.page === 'results' && (
          <Source type='geojson' data={parse(staticFeature)}>
            <Layer {...staticFeatureStyle} />
          </Source>
        )}
        {detailsParcelGeom && (
          <Source type='geojson' data={detailsParcelGeom}>
            <Layer {...focusedParcelStyle} />
          </Source>
        )}
        {pinsEnabled && showPins && pinIconsReady && (
          // No minzoom: unlike result points, pins stay visible zoomed out, down to
          // the whole US. There, nearby pins merge into a numbered bubble; a pin
          // with no neighbour is still drawn in its own colour.
          <Source
            id={PIN_SOURCE_ID}
            type='geojson'
            data={pinsGeoJson}
            cluster
            clusterMaxZoom={PIN_CLUSTER_MAX_ZOOM}
            clusterRadius={PIN_CLUSTER_RADIUS}
          >
            <Layer
              id={PIN_CLUSTER_LAYER_ID}
              type='circle'
              filter={['has', 'point_count']}
              paint={{
                'circle-color': PIN_CLUSTER_COLOR,
                'circle-radius': ['step', ['get', 'point_count'], 14, 10, 17, 50, 21, 200, 25],
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 2
              }}
            />
            <Layer
              id={PIN_CLUSTER_COUNT_LAYER_ID}
              type='symbol'
              filter={['has', 'point_count']}
              layout={{
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-allow-overlap': true,
                'text-ignore-placement': true
              }}
              paint={{ 'text-color': '#fff' }}
            />
            {/* A map-pin marker per status, tip on the property; always drawn,
                even where pins overlap, lower ones in front. */}
            <Layer
              id={PIN_LAYER_ID}
              type='symbol'
              filter={['!', ['has', 'point_count']]}
              layout={{
                'icon-image': PIN_ICON_EXPRESSION,
                'icon-anchor': 'bottom',
                'icon-offset': [0, PIN_ICON_TIP_INSET],
                'icon-size': ['interpolate', ['linear'], ['zoom'], 3, 0.8, 10, 0.9, 14, 1],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }}
            />
          </Source>
        )}
        {selectedPin && (
          // closeOnClick off: handleClick already clears the selection on any other
          // click, and Mapbox's own close would race it when another pin is clicked.
          <Popup
            key={selectedPin.id}
            longitude={selectedPin.longitude}
            latitude={selectedPin.latitude}
            anchor='bottom'
            offset={PIN_POPUP_OFFSET}
            // Room for the pin chip (its menu opens below) and the × on one line
            // (the content itself caps at 300px; Mapbox's default would be 240px).
            maxWidth='300px'
            className={styles.pinPopup}
            closeOnClick={false}
            onClose={() => setSelectedPinId(null)}
          >
            <PinPopupContent pin={selectedPin} />
          </Popup>
        )}
        {pinsEnabled && (
          <PinLegend
            features={pinsGeoJson.features}
            visible={showPins}
            onToggle={toggleShowPins}
            placers={placers}
            placedBy={placedBy}
            onPlacedBy={choosePlacedBy}
            // Below the search filter row on the bare search map; elsewhere near the
            // top (the custom Mapbox logo it once cleared is display: none).
            top={searchBarCover || 12}
          />
        )}
      </Map>
      {
        props.page !== 'owner' &&
        <div className={styles.changeBasemap} onClick={changeBasemap}>
          <img
            src={basemap === mapStyles.satellite ? streetsIcon : satelliteIcon}
            alt='basemap'
          ></img>
        </div>
      }

    </div>
  );
}
